import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TierDot } from "@/components/school/TierDot";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Clock3,
  LayoutGrid,
  Link2,
  List,
  MousePointerClick,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Target,
} from "lucide-react";
import { toast } from "sonner";
import TwoPaneLayout from "@/components/TwoPaneLayout";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SchoolPortfolioCard } from "@/components/pipeline/SchoolPortfolioCard";
import { SchoolPortfolioList } from "@/components/pipeline/SchoolPortfolioList";
import { ListBalance } from "@/components/pipeline/ListBalance";
import { SchoolPortfolioDetail } from "@/components/pipeline/SchoolPortfolioDetail";
import { CoachIcon } from "@/components/CoachIcon";
import { DreamSchoolLogoCollage } from "@/components/onboarding/DreamSchoolLogoCollage";
import {
  entryIdForSchoolParam,
  PIPELINE_STAGES,
  sortPipelineEntries,
  type PipelineSortOption,
} from "@/components/pipeline/pipelineDesign";
import { useEndUser } from "@/contexts/EndUserContext";
import {
  fetchPipeline,
  generateRecruitingPlan,
  PIPELINE_UPDATED_EVENT,
  refreshPipelineGolfFit,
  removePipelineEntry,
  updatePipelineStage,
  type PipelineEntry,
  type PipelineListResponse,
  type PipelineStage,
  type SchoolTier,
} from "@/services/pipeline";
import { fetchSchoolNotes, upsertSchoolNote } from "@/services/schoolNotes";
import {
  trackPipelineNoteSaved,
  trackPipelineSchoolRemoved,
  trackPipelineStageChanged,
  trackPipelineViewed,
} from "@/analytics/analytics";
import { PAGE_SUBTITLE_CLASS, PAGE_TITLE_CLASS } from "@/constants/design";

type FitFilter = "all" | SchoolTier;

interface PipelineLocationState {
  initialPortfolioSetup?: boolean;
  portfolioSize?: number;
}

function mergePipelineEntry(current: PipelineEntry, updated: PipelineEntry): PipelineEntry {
  return {
    ...current,
    ...updated,
    school: { ...current.school, ...updated.school },
    golfFit: updated.golfFit || current.golfFit,
  };
}

function PipelineSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3">
      {[0, 1, 2, 3].map(index => (
        <div
          key={index}
          className="h-[430px] animate-pulse overflow-hidden rounded-[20px] border border-border bg-white"
        >
          <div className="h-[132px] bg-muted" />
          <div className="space-y-3 p-5">
            <div className="h-4 w-28 rounded bg-muted" />
            <div className="h-28 rounded-2xl bg-muted" />
            <div className="grid grid-cols-2 gap-2">
              <div className="h-12 rounded-xl bg-muted" />
              <div className="h-12 rounded-xl bg-muted" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function InitialPortfolioGuide({ total, readyCount }: { total: number; readyCount: number }) {
  const progress = total > 0 ? Math.round((readyCount / total) * 100) : 0;
  const allReady = readyCount === total;

  return (
    <section className="mt-5 overflow-hidden rounded-[24px] border border-forest-soft bg-[linear-gradient(135deg,#173127_0%,#214b39_62%,#2d6549_100%)] text-white shadow-[0_18px_42px_rgba(23,49,39,0.16)]">
      <div className="grid gap-6 p-5 sm:p-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(420px,0.75fr)] xl:items-center">
        <div className="flex min-w-0 items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary text-forest shadow-[0_8px_22px_rgba(0,0,0,0.16)]"
          >
            <CoachIcon className="h-9 w-9" />
          </span>
          <div>
            <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-primary">
              Your AI Coach · First look
            </p>
            <h2 className="mt-1 font-display text-2xl font-extrabold tracking-[-0.035em] sm:text-3xl">
              Your starting school list is ready.
            </h2>
            <p className="mt-3 max-w-3xl text-xs leading-relaxed text-sage sm:text-sm">
              Your starting portfolio can include up to 50 programs across Dream, Realistic, and
              Safety. We recommend that you start wide, then narrow the list as recruiting
              progresses.
            </p>
            <p className="mt-2 max-w-3xl text-xs leading-relaxed text-sage sm:text-sm">
              Golf Fit scores and detailed assessments will appear below as they finish. This can
              take a few minutes. Click any school card to see the full assessment or remove it from
              My Schools.
            </p>
            <p className="mt-2 max-w-3xl text-xs leading-relaxed text-sage sm:text-sm">
              When new results from JGS-ranked tournaments appear on your linked profile,
              golfrecruit.ai refreshes Golf Fit for the schools already here. If your profile
              changes meaningfully, we’ll email additional programs to consider.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm sm:p-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-primary">
                Golf Fit progress
              </p>
              <p className="mt-1 text-sm font-extrabold text-white">
                {allReady
                  ? `All ${total} Golf Fit assessments are ready`
                  : `${readyCount} of ${total} Golf Fit assessments ready`}
              </p>
            </div>
            {allReady ? (
              <CheckCircle2 className="h-6 w-6 shrink-0 text-primary" />
            ) : (
              <Clock3 className="h-6 w-6 shrink-0 text-amber" />
            )}
          </div>
          <div
            className="mt-3 h-2 overflow-hidden rounded-full bg-black/25"
            role="progressbar"
            aria-label="Golf Fit assessment progress"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={readyCount}
          >
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-4 grid gap-2 text-[11px] leading-relaxed text-sage sm:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3">
            <div className="flex items-start gap-2 rounded-xl bg-black/10 p-2.5">
              <Target className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <span>Start broad across all three fit tiers</span>
            </div>
            <div className="flex items-start gap-2 rounded-xl bg-black/10 p-2.5">
              <MousePointerClick className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <span>Open or remove any school card</span>
            </div>
            <div className="flex items-start gap-2 rounded-xl bg-black/10 p-2.5">
              <RefreshCw className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <span>Fit refreshes as your results change</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function EmptyPortfolio({
  filtered,
  generating,
  onGenerate,
  gender,
  missingBasics = [],
}: {
  filtered: boolean;
  generating?: boolean;
  onGenerate?: () => void;
  gender?: string | null;
  missingBasics?: string[];
}) {
  if (filtered) {
    return (
      <div className="rounded-[20px] border border-dashed border-border-strong bg-white/60 px-6 py-16 text-center">
        <h2 className="font-display text-2xl font-extrabold tracking-[-0.035em] text-forest">
          No schools match these filters
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          Try another school name, fit tier, or relationship stage.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl rounded-[24px] border border-border bg-white px-6 py-14 text-center shadow-[0_14px_40px_rgba(26,43,35,0.06)]">
      <DreamSchoolLogoCollage gender={gender} />
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-lime-soft text-primary-text">
        <Sparkles className="h-6 w-6" />
      </div>
      <h2 className="mt-5 font-display text-3xl font-extrabold tracking-[-0.045em] text-forest">
        Ready for a fresh school list?
      </h2>
      <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground">
        We’ll use everything in your recruiting profile to build a starting portfolio. You can
        remove, add, and move every school as your search evolves.
      </p>
      {missingBasics.length > 0 && (
        <p className="mx-auto mt-4 max-w-lg rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900">
          Add {missingBasics.join(", ")} in your{" "}
          <Link to="/profile" className="font-extrabold underline">
            profile
          </Link>{" "}
          before generating a list.
        </p>
      )}
      <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
        <Button onClick={onGenerate} disabled={generating || missingBasics.length > 0}>
          <Sparkles className="h-4 w-4" />
          {generating ? "Generating…" : "Generate my schools"}
        </Button>
        <Button variant="outline" asChild>
          <Link to="/schools">
            <Search className="h-4 w-4" /> Search schools
          </Link>
        </Button>
      </div>
      <Link
        to="/chat"
        state={{ autoPrompt: "Help me decide which college golf programs I should investigate." }}
        className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-primary-text hover:underline"
      >
        Investigate with AI Coach <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

function FoundationBanner({ onExploreMore }: { onExploreMore: () => void }) {
  const [showExample, setShowExample] = useState(false);
  return (
    <section className="mt-5 overflow-hidden rounded-[22px] border border-lime-border bg-[linear-gradient(135deg,#173127_0%,#244b39_100%)] text-white shadow-[0_16px_36px_rgba(23,49,39,0.14)]">
      <div className="grid gap-5 p-5 sm:p-6 lg:grid-cols-[1fr_auto] lg:items-center">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/15 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-primary">
            <Target className="h-3.5 w-3.5" /> Your foundation phase
          </div>
          <h2 className="font-display text-2xl font-extrabold tracking-[-0.035em]">
            A JGS ranking unlocks your golf-fit scores.
          </h2>
          <p className="mt-2 max-w-3xl text-xs leading-relaxed text-sage sm:text-sm">
            Without a JGS class rank and scoring differential, we can’t reliably compare your game
            with each program’s recruits yet. That’s okay—your AI Coach can explain what JGS is, why
            it matters, and walk you step by step toward your first ranking.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 lg:max-w-[360px] lg:justify-end">
          <Button asChild>
            <Link
              to="/chat"
              state={{
                autoPrompt:
                  "I don’t have a Junior Golf Scoreboard (JGS) ranking yet. First, explain in simple terms what JGS is, how college coaches use it, and why having a ranking matters for recruiting. Assume I know nothing about JGS. Then explain how a player becomes eligible for a ranking and use my current profile to build a practical, step-by-step plan for earning my first ranking, including what tournament activity or results I may need and what I should do next. Include clickable links to the official JGS tournament search (https://www.juniorgolfscoreboard.com/tournaments.asp) and the AJGA tournament schedule (https://www.ajga.org/schedule) as a national calendar. Based on my location, also link me to the most relevant state golf association or reputable local junior-tour calendar. Remind me to confirm that an event appears in JGS before registering, and do not describe a tournament as JGS-ranked unless you can verify it.",
              }}
            >
              Help me get ranked
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="border-white/25 bg-white/10 text-white hover:bg-white/15 hover:text-white"
          >
            <Link to="/profile">
              <Link2 className="mr-2 h-4 w-4" /> Review JGS details
            </Link>
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onExploreMore}
            className="text-sage hover:bg-white/10 hover:text-white"
          >
            Explore more programs
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setShowExample(value => !value)}
        className="flex w-full items-center justify-center gap-2 border-t border-white/10 px-5 py-3 text-[11px] font-bold text-sage hover:bg-white/5"
      >
        See what fit assessment unlocks
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${showExample ? "rotate-180" : ""}`}
        />
      </button>
      {showExample && (
        <div className="border-t border-white/10 bg-black/10 px-5 py-4 sm:px-6">
          <div className="max-w-2xl rounded-2xl border border-white/15 bg-white/10 p-4">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-primary">
              Example only
            </p>
            <p className="mt-2 text-sm font-extrabold">Golf Fit: 74 / 100 — Realistic</p>
            <p className="mt-1 text-[11px] leading-relaxed text-sage">
              Once your verified data is available, the AI Coach explains the score using your
              current results, class-year runway, and that program’s recruiting history.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

type PortfolioView = "cards" | "list";
const VIEW_STORAGE_KEY = "recruitai:my-schools-view";

function readSavedView(): PortfolioView {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === "list" ? "list" : "cards";
  } catch {
    return "cards";
  }
}

function PipelineContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useEndUser();
  const [data, setData] = useState<PipelineListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [notesMap, setNotesMap] = useState<Map<string, string>>(new Map());
  const [query, setQuery] = useState("");
  const [fitFilter, setFitFilter] = useState<FitFilter>("all");
  const [stageFilter, setStageFilter] = useState<"all" | PipelineStage>("all");
  const [sort, setSort] = useState<PipelineSortOption>("golf");
  const [view, setView] = useState<PortfolioView>(readSavedView);
  const [generating, setGenerating] = useState(false);
  const refreshesInFlight = useRef(new Set<string>());
  // The title and the filter bar both stick while the list scrolls (matching School
  // Search); the filter bar pins just below the title, so track the title's height.
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const observer = new ResizeObserver(() => setHeaderHeight(header.offsetHeight));
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const linkedJgsProfile = useMemo(() => {
    try {
      return sessionStorage.getItem("recruit_ai_coach_active_profile") || undefined;
    } catch {
      return undefined;
    }
  }, []);

  const loadPipeline = useCallback(async () => {
    try {
      const response = await fetchPipeline({ linkedJgsProfile });
      setData(response);
    } catch (error) {
      console.error("Failed to load pipeline:", error);
      toast.error("My Schools could not be loaded");
    } finally {
      setLoading(false);
    }
  }, [linkedJgsProfile]);

  useEffect(() => {
    trackPipelineViewed();
  }, []);

  useEffect(() => {
    loadPipeline();
  }, [loadPipeline]);

  // ?school=<teamId>: open that school once the list has loaded, then drop
  // the parameter so closing the pane does not reopen it.
  useEffect(() => {
    const teamId = searchParams.get("school");
    if (!teamId || !data) return;
    const entryId = entryIdForSchoolParam(data.pipeline, teamId);
    if (entryId) setSelectedId(entryId);
    const next = new URLSearchParams(searchParams);
    next.delete("school");
    setSearchParams(next, { replace: true });
  }, [data, searchParams, setSearchParams]);

  useEffect(() => {
    const refreshAfterGeneration = () => {
      setLoading(true);
      void loadPipeline();
    };
    window.addEventListener(PIPELINE_UPDATED_EVENT, refreshAfterGeneration);
    return () => window.removeEventListener(PIPELINE_UPDATED_EVENT, refreshAfterGeneration);
  }, [loadPipeline]);

  useEffect(() => {
    let cancelled = false;
    fetchSchoolNotes({ linkedJgsProfile })
      .then(response => {
        if (cancelled) return;
        setNotesMap(new Map(response.notes.map(note => [note.teamId, note.notes])));
      })
      .catch(error => console.error("Failed to load school notes:", error));
    return () => {
      cancelled = true;
    };
  }, [linkedJgsProfile]);

  const refreshFit = useCallback(
    async (entry: PipelineEntry) => {
      if (refreshesInFlight.current.has(entry._id)) return;
      refreshesInFlight.current.add(entry._id);
      setData(current =>
        current
          ? {
              ...current,
              pipeline: current.pipeline.map(item =>
                item._id === entry._id
                  ? {
                      ...item,
                      golfFit: {
                        status: "updating",
                        score: item.golfFit?.score ?? null,
                        tier: item.golfFit?.tier ?? null,
                        summary: item.golfFit?.summary ?? null,
                        explanation: item.golfFit?.explanation ?? null,
                        assessedAt: item.golfFit?.assessedAt ?? null,
                        evidence: item.golfFit?.evidence ?? null,
                      },
                    }
                  : item
              ),
            }
          : current
      );

      try {
        const updated = await refreshPipelineGolfFit(entry._id, linkedJgsProfile);
        setData(current =>
          current
            ? {
                ...current,
                pipeline: current.pipeline.map(item =>
                  item._id === entry._id ? mergePipelineEntry(item, updated) : item
                ),
              }
            : current
        );
      } catch (error) {
        console.error(`Failed to refresh golf fit for ${entry.school.schoolName}:`, error);
        setData(current =>
          current
            ? {
                ...current,
                pipeline: current.pipeline.map(item =>
                  item._id === entry._id
                    ? {
                        ...item,
                        golfFit: {
                          status: "failed",
                          score: item.golfFit?.score ?? null,
                          tier: item.golfFit?.tier ?? null,
                          summary: item.golfFit?.summary ?? null,
                          explanation: item.golfFit?.explanation ?? null,
                          assessedAt: item.golfFit?.assessedAt ?? null,
                          evidence: item.golfFit?.evidence ?? null,
                          error: "The AI Coach assessment could not be refreshed.",
                        },
                      }
                    : item
                ),
              }
            : current
        );
      } finally {
        refreshesInFlight.current.delete(entry._id);
      }
    },
    [linkedJgsProfile]
  );

  // Fill or refresh assessments in a small queue. The server marks an artifact stale
  // when the player's golf data or the program benchmarks have changed.
  useEffect(() => {
    if (!data || data.hasUsableGolfFitData === false) return;
    const availableSlots = Math.max(0, 2 - refreshesInFlight.current.size);
    if (availableSlots === 0) return;
    const next = data.pipeline
      .filter(entry => {
        const status = entry.golfFit?.status || "missing";
        return (
          (status === "missing" || status === "stale") && !refreshesInFlight.current.has(entry._id)
        );
      })
      .slice(0, availableSlots);
    next.forEach(entry => void refreshFit(entry));
  }, [data, refreshFit]);

  const generatePortfolio = useCallback(async () => {
    setGenerating(true);
    try {
      const result = await generateRecruitingPlan();
      await loadPipeline();
      toast.success(
        result.mode === "exploration"
          ? "Your exploration portfolio is ready"
          : "Your matched school portfolio is ready"
      );
    } catch (error) {
      toast.error("Your school list could not be generated. Check your profile and try again.");
    } finally {
      setGenerating(false);
    }
  }, [loadPipeline]);

  function handleRefreshAll() {
    setData(current =>
      current
        ? {
            ...current,
            pipeline: current.pipeline.map(entry => {
              if (refreshesInFlight.current.has(entry._id)) return entry;
              return {
                ...entry,
                golfFit: {
                  status: "stale",
                  score: entry.golfFit?.score ?? null,
                  tier: entry.golfFit?.tier ?? null,
                  summary: entry.golfFit?.summary ?? null,
                  explanation: entry.golfFit?.explanation ?? null,
                  assessedAt: entry.golfFit?.assessedAt ?? null,
                  inputHash: entry.golfFit?.inputHash ?? null,
                  evidence: entry.golfFit?.evidence ?? null,
                },
              };
            }),
          }
        : current
    );
  }

  const selectedEntry = useMemo(
    () => data?.pipeline.find(entry => entry._id === selectedId) || null,
    [data, selectedId]
  );

  const fitCounts = useMemo(() => {
    const counts: Record<SchoolTier, number> = { dream: 0, realistic: 0, safety: 0 };
    for (const entry of data?.pipeline || []) {
      if (entry.golfFit?.tier) counts[entry.golfFit.tier] += 1;
    }
    return counts;
  }, [data]);

  const visibleEntries = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const entries = (data?.pipeline || []).filter(entry => {
      const searchMatches =
        !normalizedQuery || entry.school.schoolName.toLowerCase().includes(normalizedQuery);
      const fitMatches = fitFilter === "all" || entry.golfFit?.tier === fitFilter;
      const stageMatches = stageFilter === "all" || entry.stage === stageFilter;
      return searchMatches && fitMatches && stageMatches;
    });

    return sortPipelineEntries(entries, sort);
  }, [data, fitFilter, query, sort, stageFilter]);

  const refreshingCount = (data?.pipeline || []).filter(entry =>
    ["missing", "updating", "stale"].includes(entry.golfFit?.status || "missing")
  ).length;
  const readyCount = (data?.pipeline || []).filter(
    entry => entry.golfFit?.status === "ready"
  ).length;

  async function handleStageChange(entry: PipelineEntry, stage: PipelineStage) {
    if (!data || entry.stage === stage) return;
    const previousStage = entry.stage;
    trackPipelineStageChanged({
      schoolName: entry.school.schoolName,
      fromStage: previousStage,
      toStage: stage,
    });
    setData(current =>
      current
        ? {
            ...current,
            pipeline: current.pipeline.map(item =>
              item._id === entry._id ? { ...item, stage } : item
            ),
            counts: {
              ...current.counts,
              [previousStage]: Math.max(0, current.counts[previousStage] - 1),
              [stage]: current.counts[stage] + 1,
            },
          }
        : current
    );
    try {
      await updatePipelineStage(entry._id, stage, linkedJgsProfile);
    } catch (error) {
      toast.error("The relationship stage could not be updated");
      await loadPipeline();
    }
  }

  async function handleRemove(entry: PipelineEntry) {
    if (!data) return;
    trackPipelineSchoolRemoved({ schoolName: entry.school.schoolName, stage: entry.stage });
    setSelectedId(current => (current === entry._id ? null : current));
    setData(current =>
      current
        ? {
            ...current,
            pipeline: current.pipeline.filter(item => item._id !== entry._id),
            counts: {
              ...current.counts,
              [entry.stage]: Math.max(0, current.counts[entry.stage] - 1),
            },
          }
        : current
    );
    try {
      await removePipelineEntry(entry._id, linkedJgsProfile);
      toast.success(`${entry.school.schoolName} removed from My Schools`);
    } catch (error) {
      toast.error("The school could not be removed");
      await loadPipeline();
    }
  }

  async function handleSaveNotes(entry: PipelineEntry, notes: string) {
    const teamId = entry.school.teamId || entry._id;
    const previous = notesMap.get(teamId) || "";
    setNotesMap(current => {
      const next = new Map(current);
      if (notes.trim()) next.set(teamId, notes);
      else next.delete(teamId);
      return next;
    });
    trackPipelineNoteSaved({ schoolName: entry.school.schoolName });
    try {
      await upsertSchoolNote(teamId, notes, entry.school.schoolName, linkedJgsProfile);
    } catch (error) {
      setNotesMap(current => {
        const next = new Map(current);
        if (previous) next.set(teamId, previous);
        else next.delete(teamId);
        return next;
      });
      toast.error("The note could not be saved");
      throw error;
    }
  }

  function changeView(next: PortfolioView) {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Private browsing or blocked storage: the choice just won't persist.
    }
  }

  const total = data?.pipeline.length || 0;
  const locationState = location.state as PipelineLocationState | null;
  const isInitialPortfolioSetup = Boolean(
    locationState?.initialPortfolioSetup && total > 0 && data?.hasUsableGolfFitData !== false
  );
  const filtersActive = !!query.trim() || fitFilter !== "all" || stageFilter !== "all";
  const userProfile = (currentUser as any)?.profile || {};
  const missingBasics = [
    !userProfile.displayName ? "player name" : null,
    !userProfile.gradYear ? "graduation year" : null,
    !userProfile.homeState ? "home state" : null,
    !userProfile.gender ? "gender" : null,
  ].filter(Boolean) as string[];

  return (
    <div className="relative h-full min-h-0 overflow-y-auto bg-surface">
      <div className="mx-auto w-full max-w-[1540px] px-4 pb-16 sm:px-6 lg:px-8">
        <header
          ref={headerRef}
          className="sticky top-0 z-20 -mx-4 flex items-end justify-between gap-4 bg-surface px-4 pb-1 pt-6 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8"
        >
          <div className="min-w-0">
            <h1 className={PAGE_TITLE_CLASS}>My Schools</h1>
            {total > 0 && data?.hasUsableGolfFitData !== false && !isInitialPortfolioSetup ? (
              <p className="mt-2 flex flex-wrap items-center gap-x-1.5 text-[13px] text-muted-foreground">
                <span
                  aria-hidden="true"
                  className={`inline-block h-1.5 w-1.5 rounded-full bg-primary ${refreshingCount > 0 ? "animate-pulse" : ""}`}
                />
                {refreshingCount > 0 ? (
                  <span>
                    {`Updating ${refreshingCount} golf-fit assessment${refreshingCount === 1 ? "" : "s"}`}
                  </span>
                ) : (
                  <span>Golf fit is current</span>
                )}
                {refreshingCount === 0 && (
                  <>
                    <span aria-hidden="true">·</span>
                    <button
                      type="button"
                      onClick={handleRefreshAll}
                      className="font-semibold text-primary-text hover:underline"
                    >
                      Refresh
                    </button>
                  </>
                )}
              </p>
            ) : (
              <p className={PAGE_SUBTITLE_CLASS}>
                See where your game fits today, then keep every recruiting relationship moving.
              </p>
            )}
          </div>
          <Button asChild className="shrink-0">
            <Link to="/schools">
              <Plus className="h-4 w-4 sm:hidden" />
              Find schools
              <ArrowRight className="hidden h-4 w-4 sm:block" />
            </Link>
          </Button>
        </header>

        {data && data.hasUsableGolfFitData === false && (
          <FoundationBanner onExploreMore={() => navigate("/schools")} />
        )}

        {data?.hasUsableGolfFitData &&
          data.pipeline.some(entry => entry.recommendationContext === "exploration") && (
            <div className="mt-5 flex flex-col justify-between gap-3 rounded-2xl border border-lime-border bg-lime-soft px-4 py-4 sm:flex-row sm:items-center">
              <div>
                <strong className="text-sm text-forest">Your ranking is ready.</strong>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Keep every exploration school and top up this portfolio with matched programs.
                </p>
              </div>
              <Button
                onClick={() => void generatePortfolio()}
                disabled={generating}
                className="shrink-0"
              >
                {generating ? "Building…" : "Build my matched portfolio"}
              </Button>
            </div>
          )}

        {isInitialPortfolioSetup && <InitialPortfolioGuide total={total} readyCount={readyCount} />}

        {total > 0 && data?.hasUsableGolfFitData !== false && !isInitialPortfolioSetup && (
          <ListBalance
            total={total}
            counts={fitCounts}
            showSuggestion={refreshingCount === 0}
            onPrompt={(autoPrompt, threadTitle) =>
              navigate("/chat", { state: { autoPrompt, threadTitle } })
            }
          />
        )}

        {total > 0 && (
          <div
            style={{ top: headerHeight }}
            className="sticky z-20 -mx-4 mt-1 flex flex-col gap-3 border-b border-border bg-surface px-4 pb-3 pt-3 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 xl:flex-row xl:items-center xl:justify-between"
          >
            <div className="flex min-w-0 gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {(
                [
                  ["all", "All", total],
                  ["dream", "Dream", fitCounts.dream],
                  ["realistic", "Realistic", fitCounts.realistic],
                  ["safety", "Safety", fitCounts.safety],
                ] as Array<[FitFilter, string, number]>
              ).map(([value, label, count]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFitFilter(value)}
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-2 text-[11px] font-bold transition ${fitFilter === value ? "border-forest bg-forest text-white" : "border-border bg-white text-muted-foreground hover:border-border-strong"}`}
                >
                  <TierDot tier={value} />
                  {label} <span className="ml-1 font-mono text-[11px] opacity-65">{count}</span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 sm:grid-cols-[minmax(0,1fr)_130px_140px_auto]">
              <div className="relative col-span-2 sm:col-span-1">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-3.5 w-3.5 text-subtle" />
                <Input
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Search schools"
                  aria-label="Search My Schools"
                  className="h-9 rounded-[10px] border-border bg-white pl-9 text-[11px]"
                />
              </div>
              <Select
                value={stageFilter}
                onValueChange={value => setStageFilter(value as "all" | PipelineStage)}
              >
                <SelectTrigger
                  aria-label="Filter by stage"
                  className="h-9 rounded-[10px] border-border bg-white text-[11px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stages</SelectItem>
                  {PIPELINE_STAGES.map(stage => (
                    <SelectItem key={stage.id} value={stage.id}>
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sort} onValueChange={value => setSort(value as PipelineSortOption)}>
                <SelectTrigger
                  aria-label="Sort schools"
                  className="col-span-2 h-9 rounded-[10px] border-border bg-white text-[11px] sm:col-span-1"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="golf">Top golf schools</SelectItem>
                  <SelectItem value="fit">Best golf fit</SelectItem>
                  <SelectItem value="updated">Recently updated</SelectItem>
                  <SelectItem value="stage">Recruiting stage</SelectItem>
                  <SelectItem value="name">School name</SelectItem>
                </SelectContent>
              </Select>
              <div
                role="group"
                aria-label="View"
                className="col-start-3 row-start-1 flex h-9 overflow-hidden rounded-[10px] border border-border bg-white sm:col-start-auto sm:row-start-auto"
              >
                {(
                  [
                    ["cards", "Cards", LayoutGrid],
                    ["list", "List", List],
                  ] as const
                ).map(([value, label, Icon]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={view === value}
                    aria-label={`${label} view`}
                    onClick={() => changeView(value)}
                    className={`inline-flex flex-1 items-center justify-center gap-1.5 px-3 text-xs transition-colors ${view === value ? "bg-muted font-bold text-forest" : "font-semibold text-muted-foreground hover:text-forest"}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4">
          {loading ? (
            <PipelineSkeleton />
          ) : total === 0 ? (
            <EmptyPortfolio
              filtered={false}
              generating={generating}
              onGenerate={() => void generatePortfolio()}
              gender={(currentUser as any)?.profile?.gender}
              missingBasics={missingBasics}
            />
          ) : visibleEntries.length === 0 ? (
            <EmptyPortfolio filtered={filtersActive} />
          ) : view === "list" ? (
            <SchoolPortfolioList
              entries={visibleEntries}
              onOpen={item => setSelectedId(item._id)}
            />
          ) : (
            <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3">
              {visibleEntries.map(entry => (
                <SchoolPortfolioCard
                  key={entry._id}
                  entry={entry}
                  onOpen={item => setSelectedId(item._id)}
                  onRemove={item => void handleRemove(item)}
                  onRefreshFit={item => void refreshFit(item)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <SchoolPortfolioDetail
        entry={selectedEntry}
        notes={
          selectedEntry ? notesMap.get(selectedEntry.school.teamId || selectedEntry._id) || "" : ""
        }
        onClose={() => setSelectedId(null)}
        onStageChange={(entry, stage) => void handleStageChange(entry, stage)}
        onRefreshFit={entry => void refreshFit(entry)}
        onSaveNotes={handleSaveNotes}
        onRemove={entry => void handleRemove(entry)}
      />
    </div>
  );
}

const Pipeline = () => {
  const navigate = useNavigate();
  const { isSidebarOpen, closeSidebar, chatHistory, playerSummary } = useEndUser();

  return (
    <TwoPaneLayout
      isSidebarOpen={isSidebarOpen}
      onCloseSidebar={closeSidebar}
      sidebar={
        <Sidebar
          chatHistory={chatHistory}
          onNewChat={() => navigate("/chat")}
          onClose={closeSidebar}
          onSelectChat={id => navigate(`/chat?thread=${id}`)}
          isLinked={playerSummary.isLinked}
        />
      }
      main={<PipelineContent />}
    />
  );
};

export default Pipeline;
